import React, { useState, useEffect, useRef } from 'react';
import { Building2, ChevronDown, Check, Users, Shield } from 'lucide-react';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';

interface Empresa {
  id: string;
  nome: string;
  cnpj: string;
  tipo_acesso: string;
  ativo: boolean;
  tipo_comissao?: string;
  cnae_principal?: string;
  data_termino_mandato?: string;
  status?: string;
}

interface EmpresaSelectorProps {
  onEmpresaChange: (empresa: Empresa | null) => void;
  empresaSelecionada: Empresa | null;
}

const EmpresaSelector: React.FC<EmpresaSelectorProps> = ({ onEmpresaChange, empresaSelecionada }) => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const { user } = useAuth();
  const lastLoadRef = useRef<string>('');


  // Carregar empresas do usuário
  const carregarEmpresas = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Se for super admin, carregar todas as empresas
      if (user.tipo_usuario === 'SUPER_ADMIN') {
        const { data, error } = await supabase
          .from('cp_empresas')
          .select('id, nome, cnpj, status, data_termino_mandato, tipo_comissao, cnae_principal')
          .eq('status', 'ATIVA')
          .order('nome');

        if (error) throw error;

        // Filtrar duplicatas mantendo apenas uma entrada por empresa
        const empresasUnicas = new Map();
        data?.forEach(empresa => {
          const chave = `${empresa.nome}-${empresa.cnpj}`;
          if (!empresasUnicas.has(chave)) {
            empresasUnicas.set(chave, empresa);
          }
        });

        const empresasMapeadas = Array.from(empresasUnicas.values()).map(empresa => {
          console.log('🏢 Empresa SUPER_ADMIN (filtrada):', empresa.nome, 'tipo_comissao:', empresa.tipo_comissao, 'CNAE:', empresa.cnae_principal);
          console.log('🔍 Dados completos da empresa SUPER_ADMIN:', empresa);
          return {
            id: empresa.id,
            nome: empresa.nome,
            cnpj: empresa.cnpj,
            tipo_acesso: 'ADMINISTRADOR',
            ativo: true,
            data_termino_mandato: empresa.data_termino_mandato,
            tipo_comissao: empresa.tipo_comissao,
            cnae_principal: empresa.cnae_principal,
            status: empresa.status
          };
        });
        setEmpresas(empresasMapeadas);
      } else if (user.tipo_usuario === 'ENTERPRISE') {
        // Para usuários ENTERPRISE, carregar apenas suas próprias empresas
        const { data, error } = await supabase
          .from('cp_empresas')
          .select('id, nome, cnpj, status, data_termino_mandato, tipo_comissao, cnae_principal')
          .eq('status', 'ATIVA')
          .eq('proprietario_id', user.id)
          .order('nome');

        if (error) throw error;

        // Filtrar duplicatas mantendo apenas uma entrada por empresa
        const empresasUnicas = new Map();
        data?.forEach(empresa => {
          const chave = `${empresa.nome}-${empresa.cnpj}`;
          if (!empresasUnicas.has(chave)) {
            empresasUnicas.set(chave, empresa);
          }
        });

        const empresasMapeadas = Array.from(empresasUnicas.values()).map(empresa => {
          console.log('🏢 Empresa ENTERPRISE (filtrada):', empresa.nome, 'tipo_comissao:', empresa.tipo_comissao, 'CNAE:', empresa.cnae_principal);
          console.log('🔍 Dados completos da empresa ENTERPRISE:', empresa);
          return {
            id: empresa.id,
            nome: empresa.nome,
            cnpj: empresa.cnpj,
            tipo_acesso: 'PROPRIETARIO',
            ativo: true,
            data_termino_mandato: empresa.data_termino_mandato,
            tipo_comissao: empresa.tipo_comissao,
            cnae_principal: empresa.cnae_principal,
            status: empresa.status
          };
        });
        setEmpresas(empresasMapeadas);
      } else {
        // Para outros usuários, carregar apenas empresas com acesso
        const { data, error } = await supabase
          .from('cp_usuario_empresas')
          .select(`
            empresa_id,
            tipo_acesso,
            ativo,
            empresa:cp_empresas(
              id,
              nome,
              cnpj,
              data_termino_mandato,
              tipo_comissao,
              cnae_principal
            )
          `)
          .eq('usuario_id', user.id)
          .eq('ativo', true)
          .eq('empresa.status', 'ATIVA');

        if (error) throw error;

        // Filtrar duplicatas mantendo apenas uma entrada por empresa
        const empresasUnicas = new Map();
        data?.forEach((item: any) => {
          const chave = `${item.empresa.nome}-${item.empresa.cnpj}`;
          if (!empresasUnicas.has(chave)) {
            empresasUnicas.set(chave, item);
          }
        });

        const empresasOrdenadas = Array.from(empresasUnicas.values()).map((item: any) => {
          console.log('🏢 Empresa com acesso (filtrada):', item.empresa.nome, 'tipo_comissao:', item.empresa.tipo_comissao, 'CNAE:', item.empresa.cnae_principal);
          console.log('🔍 Dados completos da empresa:', item.empresa);

          const empresaMapeada = {
            id: item.empresa.id,
            nome: item.empresa.nome,
            cnpj: item.empresa.cnpj,
            tipo_acesso: item.tipo_acesso,
            ativo: item.ativo,
            data_termino_mandato: item.empresa.data_termino_mandato,
            tipo_comissao: item.empresa.tipo_comissao,
            cnae_principal: item.empresa.cnae_principal,
            status: item.empresa.status
          };

          console.log('📤 Empresa mapeada que será retornada:', empresaMapeada);
          return empresaMapeada;
        }).sort((a, b) => a.nome.localeCompare(b.nome));

        setEmpresas(empresasOrdenadas);
      }

      // Se há apenas uma empresa, selecionar automaticamente
      if (empresas.length === 1) {
        onEmpresaChange(empresas[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      const currentKey = `empresas-selector-${user.id}`;
      
      if (lastLoadRef.current !== currentKey) {
        console.log('🔄 useEffect carregarEmpresas - user:', user.id);
        lastLoadRef.current = currentKey;
        carregarEmpresas();
      } else {
        console.log('⏭️ Pulando carregamento empresas selector - mesma chave:', currentKey);
      }
    }
  }, [user]);

  const getTipoAcessoIcon = (tipo: string) => {
    switch (tipo) {
      case 'ADMINISTRADOR':
        return <Shield className="w-4 h-4 text-purple-600" />;
      case 'GESTOR':
        return <Users className="w-4 h-4 text-blue-600" />;
      default:
        return <Building2 className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTipoAcessoLabel = (tipo: string) => {
    switch (tipo) {
      case 'ADMINISTRADOR':
        return 'Administrador';
      case 'GESTOR':
        return 'Gestor';
      case 'VISUALIZADOR':
        return 'Visualizador';
      default:
        return 'Usuário';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-gray-600">Carregando empresas...</span>
      </div>
    );
  }

  if (empresas.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-yellow-100 rounded-lg">
        <Building2 className="w-4 h-4 text-yellow-600" />
        <span className="text-sm text-yellow-800">Nenhuma empresa encontrada</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
      >
        <Building2 className="w-4 h-4 text-gray-600" />
        <div className="flex-1 text-left">
          {empresaSelecionada ? (
            <div>
              <div className="text-sm font-medium text-gray-900">{empresaSelecionada.nome}</div>
              <div className="text-xs text-gray-500">{empresaSelecionada.cnpj}</div>
            </div>
          ) : (
            <span className="text-sm text-gray-500">Selecionar empresa</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {empresas.map((empresa) => (
            <button
              key={empresa.id}
              onClick={() => {
                console.log('🔄 Selecionando empresa:', empresa.nome, 'tipo_comissao:', empresa.tipo_comissao, 'CNAE:', empresa.cnae_principal);
                console.log('📋 Dados completos da empresa selecionada:', empresa);
                onEmpresaChange(empresa);
                setShowDropdown(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-900">{empresa.nome}</span>
                  {empresaSelecionada?.id === empresa.id && (
                    <Check className="w-4 h-4 text-green-600" />
                  )}
                </div>
                <div className="text-xs text-gray-500 ml-6">{empresa.cnpj}</div>
                <div className="flex items-center gap-1 ml-6 mt-1">
                  {getTipoAcessoIcon(empresa.tipo_acesso)}
                  <span className="text-xs text-gray-500">{getTipoAcessoLabel(empresa.tipo_acesso)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Overlay para fechar dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default EmpresaSelector;
